"use client";

import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getDirection, type Locale } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { BlurImage } from "../core";

interface ProductImageCarouselProps {
  images: Array<{ url: string; id?: string }>;
  videoUrl?: string;
  alt?: string;
  className?: string;
}

export const ProductImageCarousel = ({
  images,
  videoUrl,
  alt = "Product image",
  className,
}: ProductImageCarouselProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mainCarouselApi, setMainCarouselApi] = useState<CarouselApi>();
  const [drawerCarouselApi, setDrawerCarouselApi] = useState<CarouselApi>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Carousel scroll axis follows the active locale (RTL for fa/ar, LTR for en).
  const direction = getDirection(useLocale() as Locale);
  const t = useTranslations("nft.media");

  // Sync main carousel changes to selected index
  useEffect(() => {
    if (!mainCarouselApi) return;

    const onSelect = () => {
      const newIndex = mainCarouselApi.selectedScrollSnap();
      setSelectedIndex(newIndex);
    };

    mainCarouselApi.on("select", onSelect);
    mainCarouselApi.on("reInit", onSelect);

    return () => {
      mainCarouselApi.off("select", onSelect);
      mainCarouselApi.off("reInit", onSelect);
    };
  }, [mainCarouselApi]);

  // Sync drawer carousel changes to selected index
  useEffect(() => {
    if (!drawerCarouselApi) return;

    const onSelect = () => {
      const newIndex = drawerCarouselApi.selectedScrollSnap();
      setSelectedIndex(newIndex);
    };

    drawerCarouselApi.on("select", onSelect);
    drawerCarouselApi.on("reInit", onSelect);

    return () => {
      drawerCarouselApi.off("select", onSelect);
      drawerCarouselApi.off("reInit", onSelect);
    };
  }, [drawerCarouselApi]);

  // Handle thumbnail click
  const handleThumbnailClick = (index: number) => {
    setSelectedIndex(index);
    mainCarouselApi?.scrollTo(index);
  };

  // Handle drawer thumbnail click
  const handleDrawerThumbnailClick = (index: number) => {
    setSelectedIndex(index);
    drawerCarouselApi?.scrollTo(index);
  };

  // Image limits: show max 5 in main view
  const MAX_IMAGES = 5;
  const visibleImages = images.slice(0, MAX_IMAGES);
  const visibleThumbnails = images.slice(0, MAX_IMAGES);
  const remainingCount = images.length - MAX_IMAGES;
  const hasMoreImages = remainingCount > 0;

  if (!images || images.length === 0) {
    return (
      <div
        className={cn(
          "relative mx-auto aspect-square w-full max-w-[440px] rounded-sm overflow-hidden bg-bg-tertiary flex items-center justify-center",
          className,
        )}
      >
        <span className="text-text-disabled text-sm">{t("noImage")}</span>
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      {/* Main Image Display */}
      <div className="relative mx-auto aspect-square w-full max-w-[440px] rounded-sm overflow-hidden">
        {/* Main Image Carousel - follows locale direction, limited to 5 images */}
        <Carousel
          setApi={setMainCarouselApi}
          opts={{
            align: "start",
            loop: false,
            direction,
          }}
          className="w-full h-full"
          dir={direction}
        >
          <CarouselContent className="h-full">
            {visibleImages.map((image, index) => (
              <CarouselItem key={image.id || image.url || index}>
                <BlurImage
                  src={image.url}
                  alt={`${alt} ${index + 1}`}
                  fill
                  fit="cover"
                  wrapperClassName="aspect-square w-full"
                  priority={index === 0}
                  loading={index === 0 ? "eager" : "lazy"}
                  sizes="(min-width: 768px) 440px, 100vw"
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {/* Thumbnails - positioned at bottom, on top of image */}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-50">
            <div className="bg-bg-tertiary/60 backdrop-blur-sm rounded-sm px-2 py-2 flex gap-2">
              {visibleThumbnails.map((image, index) => {
                const isLastWithOverlay =
                  index === MAX_IMAGES - 1 && hasMoreImages;

                return (
                  <button
                    key={image.id || image.url || index}
                    onClick={() => {
                      if (isLastWithOverlay) {
                        setIsDrawerOpen(true);
                      } else {
                        handleThumbnailClick(index);
                      }
                    }}
                    className={cn(
                      "relative w-11 h-11 rounded-xs overflow-hidden transition-all",
                      selectedIndex === index
                        ? "ring-2 ring-primary"
                        : "opacity-80 hover:opacity-100",
                    )}
                    aria-label={
                      isLastWithOverlay
                        ? `View all ${images.length} images`
                        : `View image ${index + 1}`
                    }
                  >
                    <Image
                      src={image.url}
                      alt={`${alt} thumbnail ${index + 1}`}
                      fill
                      className="object-cover"
                      loading="lazy"
                      sizes="44px"
                    />
                    {/* Overlay for remaining items */}
                    {isLastWithOverlay && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <span
                          className="text-white text-sm font-semibold font-inter"
                          dir="ltr"
                        >
                          +{remainingCount}
                        </span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Drawer with all images - only rendered when there are more than 5 images */}
      {hasMoreImages && (
        <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
          <DrawerContent className="max-h-[85vh]">
            <VisuallyHidden>
              <DrawerTitle>{t("productImages")}</DrawerTitle>
            </VisuallyHidden>

            {/* Tabs for Images and Videos */}
            <Tabs
              defaultValue="images"
              syncWithHash={false}
              dir="rtl"
              className="mt-3"
            >
              <TabsList
                variant="outlined"
                className="justify-end flex-row w-fit"
                dir="rtl"
              >
                <TabsTrigger value="images" variant="outlined">
                  {t("imagesTab")}
                </TabsTrigger>
                <TabsTrigger value="videos" variant="outlined">
                  {t("videosTab")}
                </TabsTrigger>
              </TabsList>

              {/* Images Tab Content */}
              <TabsContent value="images" className="mt-4">
                <div className="relative w-full aspect-square max-h-[50vh] rounded-sm overflow-hidden">
                  <Carousel
                    setApi={setDrawerCarouselApi}
                    opts={{
                      align: "start",
                      loop: false,
                      direction,
                      startIndex: selectedIndex,
                    }}
                    className="w-full h-full"
                    dir={direction}
                  >
                    <CarouselContent className="h-full">
                      {images.map((img, idx) => (
                        <CarouselItem
                          key={img.id || img.url || idx}
                          className="h-full"
                        >
                          <BlurImage
                            src={img.url}
                            alt={`${alt} ${idx + 1}`}
                            width={500}
                            height={500}
                            wrapperClassName="w-full h-full flex items-center justify-center"
                            className="object-cover aspect-square"
                            loading="lazy"
                          />
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>
                </div>

                {/* Thumbnails in Drawer - all images, single line scrollable */}
                <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
                  {images.map((img, idx) => (
                    <button
                      key={img.id || img.url || idx}
                      onClick={() => handleDrawerThumbnailClick(idx)}
                      className="flex flex-col items-center shrink-0"
                      aria-label={`View image ${idx + 1}`}
                    >
                      <div
                        className={cn(
                          "relative w-14 h-14 rounded-xs overflow-hidden transition-all",
                          selectedIndex === idx
                            ? ""
                            : "opacity-60 hover:opacity-100",
                        )}
                      >
                        <Image
                          src={img.url}
                          alt={`${alt} thumbnail ${idx + 1}`}
                          fill
                          className="object-cover"
                          loading="lazy"
                          sizes="56px"
                        />
                      </div>
                      {/* Active indicator - 8px spacing, 4px height, accent-secondary */}
                      <div
                        className={cn(
                          "mt-2 w-full h-1 transition-all",
                          selectedIndex === idx
                            ? "bg-accent-secondary"
                            : "bg-transparent",
                        )}
                      />
                    </button>
                  ))}
                </div>
              </TabsContent>

              {/* Videos Tab Content */}
              <TabsContent value="videos" className="mt-4">
                {videoUrl ? (
                  <div className="relative w-full aspect-video rounded-sm overflow-hidden bg-black">
                    <video
                      src={videoUrl}
                      controls
                      playsInline
                      className="w-full h-full object-contain"
                      controlsList="nodownload"
                    >
                      <track kind="captions" />
                      {t("videoUnsupported")}
                    </video>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[200px] text-text-disabled">
                    {t("noVideo")}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
};
